import React, { useCallback, useEffect, useState } from 'react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import { X, Loader2, ShieldAlert, Smartphone, Snowflake, Sun, Pin, PinOff } from 'lucide-react';

const riskTone = (s) => (s >= 80 ? 'text-rose-600 bg-rose-50 border-rose-200'
  : s >= 50 ? 'text-amber-600 bg-amber-50 border-amber-200'
  : 'text-emerald-600 bg-emerald-50 border-emerald-200');

export default function FraudProfileModal({ payerId, onClose }) {
  const [p, setP] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setP(await apiCall(`/api/admin/finance/fraud-profile/${payerId}`));
    } catch (e) { setErr(e.message || 'Could not load profile'); }
  }, [payerId]);

  useEffect(() => { load(); }, [load]);

  const toggleFreeze = async () => {
    setBusy(true);
    try {
      const action = p.wallet?.frozen ? 'unfreeze' : 'freeze';
      await apiCall(`/api/admin/finance/wallets/${payerId}/${action}`, { method: 'POST' });
      toast.success(`Wallet ${action === 'freeze' ? 'frozen' : 'unfrozen'}`);
      await load();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const toggleWatchlist = async () => {
    setBusy(true);
    try {
      if (p.on_watchlist) {
        await apiCall(`/api/admin/finance/watchlist/${payerId}`, { method: 'DELETE' });
        toast.success('Removed from risk watchlist');
      } else {
        await apiCall(`/api/admin/finance/watchlist/${payerId}`, {
          method: 'POST', body: JSON.stringify({ reason: 'Pinned from fraud profile' }),
        });
        toast.success('Pinned to risk watchlist', { description: 'Admins will be alerted on their next payment.' });
      }
      await load();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="fraud-profile-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
          <h3 className="font-bold text-slate-900 flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-indigo-600" /> Payer Fraud Profile</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="fraud-profile-close"><X className="w-4 h-4" /></button>
        </div>
        {err && <p className="p-6 text-sm text-rose-500" data-testid="fraud-profile-error">{err}</p>}
        {!p && !err && <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>}
        {p && (
          <div className="p-5 space-y-4">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <p className="font-bold text-slate-900" data-testid="fraud-profile-name">{p.user.name || p.user.email}
                  {p.on_watchlist && <span className="ml-2 px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 border border-rose-200 text-[10px] font-bold align-middle" data-testid="watchlist-badge">ON WATCHLIST</span>}
                </p>
                <p className="text-xs text-slate-500">{p.user.email} · {p.user.role}</p>
                {p.wallet && (
                  <p className="text-xs mt-1">
                    Wallet <b>${p.wallet.balance.toFixed(2)}</b>
                    {p.wallet.frozen && <span className="ml-2 px-1.5 py-0.5 rounded bg-sky-50 text-sky-600 border border-sky-200 text-[10px] font-bold">FROZEN</span>}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1.5 rounded-xl border text-sm font-bold ${riskTone(p.fraud.live_score)}`} data-testid="fraud-live-score">
                  Live risk {p.fraud.live_score}
                </span>
                <button onClick={toggleWatchlist} disabled={busy}
                  className={`px-3 py-1.5 border rounded-xl text-xs font-bold inline-flex items-center gap-1 ${p.on_watchlist ? 'border-rose-200 text-rose-600 hover:bg-rose-50' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                  data-testid="watchlist-toggle">
                  {p.on_watchlist ? <><PinOff className="w-3.5 h-3.5" /> Unwatch</> : <><Pin className="w-3.5 h-3.5" /> Watchlist</>}
                </button>
                {p.wallet && (
                  <button onClick={toggleFreeze} disabled={busy}
                    className="px-3 py-1.5 border border-slate-200 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1"
                    data-testid="fraud-freeze-toggle">
                    {p.wallet.frozen ? <><Sun className="w-3.5 h-3.5" /> Unfreeze</> : <><Snowflake className="w-3.5 h-3.5" /> Freeze wallet</>}
                  </button>
                )}
              </div>
            </div>
            {p.fraud.live_reasons.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {p.fraud.live_reasons.map((r) => (
                  <span key={r} className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[11px] font-semibold">{r.replace(/_/g, ' ')}</span>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="fraud-velocity-grid">
              {Object.entries(p.velocity).map(([k, v]) => (
                <div key={k} className="bg-slate-50 rounded-xl p-3 text-center">
                  <p className="text-lg font-bold text-slate-900">{v}</p>
                  <p className="text-[10px] uppercase tracking-wide text-slate-500">{k.replace('last_', 'payments / ')}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500">30-day payment volume: <b>${p.volume_30d.toFixed(2)}</b> · avg fraud score {p.fraud.avg_score} · max {p.fraud.max_score}</p>

            <div>
              <h4 className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1.5 flex items-center gap-1"><Smartphone className="w-3.5 h-3.5" /> Devices ({p.devices.length})</h4>
              {p.devices.length === 0 ? <p className="text-xs text-slate-400">No device fingerprints recorded yet.</p> : (
                <div className="divide-y divide-slate-100 border border-slate-100 rounded-xl overflow-hidden" data-testid="fraud-devices-list">
                  {p.devices.map((d) => (
                    <div key={d.ua_hash} className="px-3 py-2 flex items-center gap-3 text-xs">
                      <code className="text-slate-700 font-mono">{d.ua_hash}</code>
                      <span className="text-slate-400">{d.count} payment(s)</span>
                      <span className="text-slate-400 flex-1 truncate">{(d.ips || []).join(', ')}</span>
                      <span className="text-slate-400 whitespace-nowrap">{d.last_seen ? new Date(d.last_seen).toLocaleDateString() : ''}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h4 className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1.5">Settlement history</h4>
              <div className="flex flex-wrap gap-2" data-testid="fraud-settlement-summary">
                {Object.keys(p.settlements).length === 0 ? <p className="text-xs text-slate-400">No settlements yet.</p>
                  : Object.entries(p.settlements).map(([st, v]) => (
                    <span key={st} className="px-2.5 py-1 rounded-lg bg-slate-50 border border-slate-200 text-xs">
                      <b className="capitalize">{st}</b>: {v.count} · ${v.value.toFixed(2)}
                    </span>
                  ))}
              </div>
            </div>

            {p.fraud.recent_flags.length > 0 && (
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1.5">Recent flags</h4>
                <div className="divide-y divide-slate-100 border border-amber-100 rounded-xl overflow-hidden" data-testid="fraud-flags-list">
                  {p.fraud.recent_flags.map((f) => (
                    <div key={f.session_id} className="px-3 py-2 text-xs flex items-center gap-2 flex-wrap bg-amber-50/40">
                      <span className="font-bold text-slate-800">${Number(f.amount || 0).toFixed(2)}</span>
                      <span className={`px-1.5 py-0.5 rounded border text-[10px] font-bold uppercase ${riskTone(f.score || 0)}`}>risk {f.score}</span>
                      <span className="text-slate-500 flex-1">{(f.reasons || []).join(', ').replace(/_/g, ' ')}</span>
                      <span className="text-slate-400">{f.status} · {f.at ? new Date(f.at).toLocaleDateString() : ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
